import { createSlice, current, PayloadAction } from "@reduxjs/toolkit";
import {
	EditMetadataItemType,
	IEditMetadataDiff,
	IEditMetadataLockHolder,
	IEditMetadataRow,
	IEditMetadataView,
	IMetadataLastEdited,
	IValueDelete,
	IValueRename,
	ICategoryRename,
} from "src/pages/EditMetadata/models/interfaces";
import { isCategoryNameTaken } from "src/pages/EditMetadata/transformer";
import { uniquifyValue } from "src/pages/EditMetadata/utils";

export type EditMetadataLoadStatus = "idle" | "loading" | "loaded" | "error" | "locked-readonly";

interface EditMetadataState {
	status: EditMetadataLoadStatus;
	studyId: string | null;
	studyName: string;
	itemType: EditMetadataItemType | null;
	originalRows: IEditMetadataRow[];
	rows: IEditMetadataRow[];
	categoryRenames: ICategoryRename[];
	categoryDeletes: string[];
	valueRenames: IValueRename[];
	valueDeletes: IValueDelete[];
	categoryOrder: string[];
	originalCategoryOrder: string[];
	pendingCategoryAdds: string[]; // local-only category columns until saved
	lock: IEditMetadataLockHolder | null;
	lockExpiredBanner: boolean;
	loadError: string | null;
	saveError: string | null;
	isSaving: boolean;
	// Captured at GET time; heartbeats compare to detect underlying changes
	// (transcripts added/removed/edited by another user mid-session).
	loadedDataVersion: string | null;
	dataChangedSinceLoad: boolean;
	metadataLastEdited: IMetadataLastEdited | null;
	// AI-generated category columns, rendered with the lavender/purple tint. A column
	// stays in this list (purple) while it is being edited — manual intervention only
	// recolors it to green once the bulk save succeeds.
	aiGeneratedCategories: string[];
	// AI-generated categories whose file data has been edited but not yet saved. Still
	// rendered purple; tracked so saveSucceeded can promote them to green (and a
	// discard can simply forget them).
	pendingManualCategories: string[];
	// Categories that were AI-generated, manually edited, and saved via edit-bulk.
	// Rendered green for the rest of the session to signal human-verified intervention.
	manuallyEditedCategories: string[];
	// True when at least one cell has been edited since the last load/save/discard.
	// Lets the page compute a cheap dirtyCount without running computeDiff on every
	// keystroke (issue #3).
	hasCellEdits: boolean;
}

const initialState: EditMetadataState = {
	status: "idle",
	studyId: null,
	studyName: "",
	itemType: null,
	originalRows: [],
	rows: [],
	categoryRenames: [],
	categoryDeletes: [],
	valueRenames: [],
	valueDeletes: [],
	categoryOrder: [],
	originalCategoryOrder: [],
	pendingCategoryAdds: [],
	lock: null,
	lockExpiredBanner: false,
	loadError: null,
	saveError: null,
	isSaving: false,
	loadedDataVersion: null,
	dataChangedSinceLoad: false,
	metadataLastEdited: null,
	aiGeneratedCategories: [],
	pendingManualCategories: [],
	manuallyEditedCategories: [],
	hasCellEdits: false,
};

const trim = (value: unknown) => String(value || "").trim();

// A file-data change inside an AI-generated category counts as manual intervention.
// The column stays purple (still AI-generated) until the bulk save lands; we only
// record it as pending here so saveSucceeded can promote it to green.
const markCategoryManuallyEdited = (state: EditMetadataState, rawCategory: string) => {
	const category = trim(rawCategory);
	if (!category || !state.aiGeneratedCategories.includes(category)) return;
	if (!state.pendingManualCategories.includes(category)) {
		state.pendingManualCategories.push(category);
	}
};

// Carry a category's tint state to its new name on rename, so the column keeps the
// purple/pending tint it had under the old name rather than dropping the entry.
const renameInCategoryList = (list: string[], from: string, to: string): string[] =>
	list.includes(from) ? Array.from(new Set(list.map((c) => (c === from ? to : c)))) : list;

const mergeCategoryOrder = (...orders: string[][]): string[] => {
	const seen = new Set<string>();
	const merged: string[] = [];
	for (const order of orders) {
		for (const categoryValue of order || []) {
			const category = trim(categoryValue);
			if (!category || seen.has(category)) continue;
			seen.add(category);
			merged.push(category);
		}
	}
	return merged;
};

const collectCategoryOrderFromRows = (rows: IEditMetadataRow[]): string[] => {
	const order: string[] = [];
	for (const row of rows || []) {
		for (const tag of row.tags || []) {
			const category = trim(tag?.tag_category);
			if (category) order.push(category);
		}
	}
	return mergeCategoryOrder(order);
};

const renameCategoryInOrder = (order: string[], from: string, to: string): string[] =>
	mergeCategoryOrder(order.map((category) => (category === from ? to : category)));

const editMetadataSlice = createSlice({
	name: "editMetadataSlice",
	initialState,
	reducers: {
		// Set by the navigation caller (Manage Studies / Add Study / Custom Segments)
		// right before routing to /edit-metadata | /view-metadata. The study id now lives
		// in Redux instead of the URL, so the page can use single-segment routes (which
		// load their bundles correctly on hard refresh). Resets prior editor state so a
		// previous study's rows don't flash. On refresh this is empty → the page bounces
		// back to Manage Studies.
		setMetadataStudyId(_state, action: PayloadAction<string>) {
			return { ...initialState, studyId: action.payload };
		},
		startLoad(state, action: PayloadAction<{ studyId: string }>) {
			state.status = "loading";
			state.studyId = action.payload.studyId;
			state.loadError = null;
		},
		loadSucceeded(state, action: PayloadAction<IEditMetadataView>) {
			const view = action.payload;
			const categoryOrder = mergeCategoryOrder(
				(view.categories || []).map((category) => category.name),
				collectCategoryOrderFromRows(view.rows),
			);
			state.status = view.lock && !view.lock.held_by_me ? "locked-readonly" : "loaded";
			state.studyName = view.study_name;
			state.itemType = view.item_type;
			state.originalRows = view.rows;
			state.rows = structuredClone(view.rows); // #7: replace slow JSON round-trip
			state.categoryRenames = [];
			state.categoryDeletes = [];
			state.valueRenames = [];
			state.valueDeletes = [];
			state.categoryOrder = categoryOrder;
			state.originalCategoryOrder = categoryOrder;
			state.pendingCategoryAdds = [];
			state.lock = view.lock;
			state.loadError = null;
			state.loadedDataVersion = view.data_version;
			state.dataChangedSinceLoad = false;
			state.metadataLastEdited = view.metadata_last_edited ?? null;
			state.aiGeneratedCategories = view.ai_generated_categories ?? [];
			state.pendingManualCategories = [];
			state.manuallyEditedCategories = [];
			state.hasCellEdits = false;
		},
		loadFailed(state, action: PayloadAction<string>) {
			state.status = "error";
			state.loadError = action.payload;
		},
		setLock(state, action: PayloadAction<IEditMetadataLockHolder | null>) {
			state.lock = action.payload;
			if (state.status === "locked-readonly" && action.payload?.held_by_me) {
				state.status = "loaded";
			}
			if (state.status === "loaded" && action.payload && !action.payload.held_by_me) {
				state.status = "locked-readonly";
			}
		},
		setLockExpiredBanner(state, action: PayloadAction<boolean>) {
			state.lockExpiredBanner = action.payload;
		},
		// Heartbeat reports the current data_version. Anything other than the
		// captured loadedDataVersion means another user mutated the study.
		recordHeartbeatVersion(state, action: PayloadAction<string | null | undefined>) {
			const incoming = action.payload || null;
			if (!state.loadedDataVersion) return;
			if (incoming && incoming !== state.loadedDataVersion) {
				state.dataChangedSinceLoad = true;
			}
		},
		// One file × one category cell edit. Replaces values for that cell.
		updateCell(
			state,
			action: PayloadAction<{ fileId: string; category: string; values: string[] }>,
		) {
			const { category, fileId, values } = action.payload;
			const cleanValues = Array.from(new Set(values.map(trim).filter(Boolean)));
			const row = state.rows.find((r) => r.file_id === fileId);
			if (!row) return;
			row.tags = row.tags.filter((t) => t.tag_category !== category);
			for (const value of cleanValues) {
				row.tags.push({ meta_id: null, tag_category: category, tag_value: value });
			}
			// #3: flag that at least one cell has been edited. The page derives a cheap
			// dirtyCount from this + op lengths instead of running computeDiff on every
			// keystroke.
			state.hasCellEdits = true;
			markCategoryManuallyEdited(state, category);
		},
		addCategory(state, action: PayloadAction<string>) {
			const name = trim(action.payload);
			if (!name) return;
			// #14: use the same case-insensitive check as AddCategoryModal so the two
			// sites can never diverge. Reducer is authoritative.
			if (isCategoryNameTaken(name, state.rows, state.pendingCategoryAdds)) return;
			state.categoryOrder = mergeCategoryOrder(state.categoryOrder, [name]);
			state.pendingCategoryAdds.push(name);
		},
		renameCategory(state, action: PayloadAction<{ from: string; to: string }>) {
			const from = trim(action.payload.from);
			const to = trim(action.payload.to);
			if (!from || !to || from === to) return;
			// Apply to in-memory rows so the grid re-renders with the new column
			for (const row of state.rows) {
				for (const tag of row.tags) if (tag.tag_category === from) tag.tag_category = to;
			}
			// Track the op for the bulk save
			const existing = state.categoryRenames.find((r) => r.from_category === from);
			if (existing) existing.to_category = to;
			else state.categoryRenames.push({ from_category: from, to_category: to });
			// Re-apply pendingCategoryAdds; dedupe in case `to` was already pending (#15)
			state.pendingCategoryAdds = Array.from(
				new Set(state.pendingCategoryAdds.map((c) => (c === from ? to : c))),
			);
			state.categoryOrder = mergeCategoryOrder(
				renameCategoryInOrder(state.categoryOrder, from, to),
				collectCategoryOrderFromRows(state.rows),
			);
			// Carry the column's tint to its new name first…
			state.aiGeneratedCategories = renameInCategoryList(state.aiGeneratedCategories, from, to);
			state.pendingManualCategories = renameInCategoryList(state.pendingManualCategories, from, to);
			state.manuallyEditedCategories = renameInCategoryList(state.manuallyEditedCategories, from, to);
			// …then treat the rename as manual intervention. The column stays purple
			// (pending) until the bulk save lands; saveSucceeded then drops it out of the
			// AI set, so it falls back to the default (green) header — its way of becoming a
			// normal, human-verified column.
			markCategoryManuallyEdited(state, to);
		},
		deleteCategory(state, action: PayloadAction<string>) {
			const category = trim(action.payload);
			if (!category) return;
			for (const row of state.rows) row.tags = row.tags.filter((t) => t.tag_category !== category);
			state.pendingCategoryAdds = state.pendingCategoryAdds.filter((c) => c !== category);
			state.categoryOrder = state.categoryOrder.filter((c) => c !== category);
			if (!state.categoryDeletes.includes(category)) state.categoryDeletes.push(category);
		},
		renameValue(state, action: PayloadAction<{ category: string; from: string; to: string }>) {
			const category = trim(action.payload.category);
			const from = trim(action.payload.from);
			const requestedTo = trim(action.payload.to);
			if (!category || !from || !requestedTo || from === requestedTo) return;
			// If the edited value collides with another existing value in the same
			// column, disambiguate copies with a bracketed counter — "value (1)",
			// "value (2)", … — instead of silently merging them.
			const existingValues = new Set<string>();
			for (const row of state.rows) {
				for (const tag of row.tags) {
					if (tag.tag_category === category && tag.tag_value !== from) {
						existingValues.add(tag.tag_value);
					}
				}
			}
			const to = uniquifyValue(requestedTo, existingValues);
			for (const row of state.rows) {
				for (const tag of row.tags) {
					if (tag.tag_category === category && tag.tag_value === from) {
						tag.tag_value = to;
					}
				}
			}
			// #13: fold rename ops — if a prior rename produced `from`, update that op
			// (A→B then B→C becomes a single A→C) instead of appending a second op
			// that the backend can't reconcile cleanly.
			const existingIdx = state.valueRenames.findIndex(
				(r) => r.tag_category === category && r.to_value === from,
			);
			if (existingIdx !== -1) {
				state.valueRenames[existingIdx].to_value = to;
			} else {
				state.valueRenames.push({ tag_category: category, from_value: from, to_value: to });
			}
			markCategoryManuallyEdited(state, category);
		},
		deleteValue(state, action: PayloadAction<{ category: string; value: string }>) {
			const category = trim(action.payload.category);
			const value = trim(action.payload.value);
			if (!category || !value) return;
			for (const row of state.rows) {
				row.tags = row.tags.filter((t) => !(t.tag_category === category && t.tag_value === value));
			}
			// #13: if a prior rename produced `value`, strip that op and delete the
			// *original* value name instead — otherwise the backend would try to delete
			// a value that never existed in the persisted data.
			const renameIdx = state.valueRenames.findIndex(
				(r) => r.tag_category === category && r.to_value === value,
			);
			if (renameIdx !== -1) {
				const originalValue = state.valueRenames[renameIdx].from_value;
				state.valueRenames.splice(renameIdx, 1);
				if (!state.valueDeletes.some((d) => d.tag_category === category && d.value === originalValue)) {
					state.valueDeletes.push({ tag_category: category, value: originalValue });
				}
			} else {
				state.valueDeletes.push({ tag_category: category, value });
			}
			markCategoryManuallyEdited(state, category);
		},
		discardAll(state) {
			// current() unwraps the Immer Proxy to a plain value before cloning.
			// structuredClone throws DataCloneError on Proxy objects (HTML spec §2.7).
			state.rows = structuredClone(current(state.originalRows)); // #7
			state.categoryRenames = [];
			state.categoryDeletes = [];
			state.valueRenames = [];
			state.valueDeletes = [];
			state.categoryOrder = [...state.originalCategoryOrder];
			state.pendingCategoryAdds = [];
			state.saveError = null;
			state.hasCellEdits = false; // #3
			// Unsaved edits are reverted. The columns stayed purple throughout, so we
			// just forget the pending manual edits; already-saved green columns persist.
			state.pendingManualCategories = [];
		},
		startSave(state) {
			state.isSaving = true;
			state.saveError = null;
		},
		saveSucceeded(
			state,
			action: PayloadAction<{ metadata_last_edited: IMetadataLastEdited | null; data_version?: string | null } | undefined>,
		) {
			state.isSaving = false;
			state.originalRows = structuredClone(current(state.rows)); // #7 — current() unwraps Proxy
			state.categoryRenames = [];
			state.categoryDeletes = [];
			state.valueRenames = [];
			state.valueDeletes = [];
			state.originalCategoryOrder = [...state.categoryOrder];
			state.pendingCategoryAdds = [];
			state.hasCellEdits = false; // #3
			state.dataChangedSinceLoad = false;
			state.lockExpiredBanner = false;
			// edit-bulk landed: the touched AI categories are now human-verified, so move
			// them out of the purple set and into the green set for the rest of the session.
			state.manuallyEditedCategories = mergeCategoryOrder(
				state.manuallyEditedCategories,
				state.pendingManualCategories,
			);
			state.aiGeneratedCategories = state.aiGeneratedCategories.filter(
				(c) => !state.pendingManualCategories.includes(c),
			);
			state.pendingManualCategories = [];
			if (action.payload && "metadata_last_edited" in action.payload) {
				state.metadataLastEdited = action.payload.metadata_last_edited ?? state.metadataLastEdited;
			}
			if (action.payload?.data_version) {
				state.loadedDataVersion = action.payload.data_version;
			}
		},
		saveFailed(state, action: PayloadAction<string>) {
			state.isSaving = false;
			state.saveError = action.payload;
		},
		resetEditMetadata: () => initialState,
	},
});

export const {
	addCategory,
	deleteCategory,
	deleteValue,
	discardAll,
	loadFailed,
	loadSucceeded,
	recordHeartbeatVersion,
	renameCategory,
	renameValue,
	resetEditMetadata,
	saveFailed,
	saveSucceeded,
	setLock,
	setLockExpiredBanner,
	setMetadataStudyId,
	startLoad,
	startSave,
	updateCell,
} = editMetadataSlice.actions;

export default editMetadataSlice.reducer;
